import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { updateError } from './user';
import { dispatch } from '../store';
import { getPracticeId } from './audienceManager';

const initialState = {
    tablePreferenceLoading: false,
    tablePreferences: [],
    globalTablePreferences: [],
    practiceTablePreferences: { results: [] }
};

const slice = createSlice({
    name: 'tablePreferences',
    initialState,
    reducers: {
        resetTablePreferences(state) {
            Object.assign(state, initialState);
        },
        clearTablePreferences(state) {
            state.tablePreferences = [];
        },
        setTablePreferenceLoading(state, action) {
            state.tablePreferenceLoading = action.payload;
        },
        setTablePreferences(state, action) {
            state.tablePreferences = action.payload;
        },
        setGlobalTablePreferences(state, action) {
            state.globalTablePreferences = action.payload;
        },
        upsertGlobalTablePreference(state, action) {
            // Insert or replace a single global pref by id (preserves the rest of
            // the array, so a successful save doesn't blank the editor list).
            const next = action.payload;
            if (!next) return;
            const list = state.globalTablePreferences?.results
                ? state.globalTablePreferences.results
                : state.globalTablePreferences || [];
            const idx = list.findIndex((r) => r?.id === next.id);
            if (idx >= 0) {
                list[idx] = next;
            } else {
                list.push(next);
            }
        },
        removeGlobalTablePreference(state, action) {
            const removeId = action.payload;
            const list = state.globalTablePreferences?.results
                ? state.globalTablePreferences.results
                : state.globalTablePreferences || [];
            const idx = list.findIndex((r) => r?.id === removeId);
            if (idx >= 0) list.splice(idx, 1);
        },
        setPracticeTablePreferences(state, action) {
            state.practiceTablePreferences.results = action.payload;
        }
    }
});

export default slice.reducer;

export const { setTablePreferences, resetTablePreferences, clearTablePreferences, setTablePreferenceLoading, setGlobalTablePreferences, setPracticeTablePreferences, upsertGlobalTablePreference, removeGlobalTablePreference } = slice.actions;

const handleStopLoading = () => {
    setTimeout(() => {
        dispatch(slice.actions.setTablePreferenceLoading(false));
    }, 500);
};

export function getTablePreferences(data) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${getPracticeId()}/users/${data?.practiceUserId}/table-column-preferences/?fields={id,table_name,preferences}`);
            handleStopLoading();
            if (response.status === 200) {
                dispatch(slice.actions.setTablePreferences(response.data));
            } else {
                dispatch(updateError({ success: false, message: response.data || 'Something went wrong' }));
            }
        } catch (error) {
            handleStopLoading();
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getGlobalTablePreferences() {
    return async () => {
        try {
            const response = await callApi('/api/v1/utils/globaltablecolumnpreference/');
            handleStopLoading();
            if (response.status === 200) {
                dispatch(slice.actions.setGlobalTablePreferences(response.data));
            } else {
                dispatch(updateError({ success: false, message: response.data || 'Something went wrong' }));
            }
        } catch (error) {
            handleStopLoading();
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getPracticeTablePreferences(practiceId) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/table-column-preferences/`);
            if (response.status === 200) {
                dispatch(slice.actions.setPracticeTablePreferences(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to load practice table preferences' }));
        }
    };
}

export function handleTablePreferences(data) {
    return async () => {
        try {
            if (!data?.body?.table_name) {
                handleStopLoading();
                return;
            }
            const response = await callApi(`/api/v1/practices/${getPracticeId()}/users/${data?.practiceUserId}/table-column-preferences/`, 'post', data.body);
            if (response.status === 201) {
                dispatch(getTablePreferences({ practiceUserId: data?.practiceUserId }));
                dispatch(updateError({ success: true, message: 'Column visibility stored successfully' }));
            } else {
                handleStopLoading();
                dispatch(updateError({ success: false, message: response?.data?.detail || 'Something went wrong' }));
            }
        } catch (error) {
            handleStopLoading();
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function handleTablePreferencesEdit(data) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${getPracticeId()}/users/${data?.practiceUserId}/table-column-preferences/${data?.tableId}`, 'patch', data.body);
            if (response.status === 200) {
                dispatch(getTablePreferences({ practiceUserId: data?.practiceUserId }));
                dispatch(updateError({ success: true, message: 'Table preference updated successfully' }));
            } else {
                handleStopLoading();
                dispatch(updateError({ success: false, message: JSON.stringify(response.data?.columns) || 'Something went wrong' }));
            }
        } catch (error) {
            handleStopLoading();
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

// ── System-default (global) write thunks ──────────────────────────────────────
// These target `/api/v1/utils/globaltablecolumnpreference/` — the system-wide
// baseline that every practice falls through to when it has no record of its
// own (see resolution cascade in src/utils/tablePreferences.js). Editing here
// affects every practice that hasn't customised the table.
//
// Body shape mirrors the per-practice endpoint minus `practice` and `target_users`:
//   { table_name, preferences: { columns, isCombinedName, preferences } }

// Extract a user-presentable error message from a non-OK response. The
// per-practice TableColumnPreference serializer returns top-level `columns`
// validation errors; the global serializer does NOT have a top-level `columns`
// field (columns live nested inside `preferences.preferences.columns`), so the
// per-practice extraction prints "undefined" here. Prefer `detail`, then any
// serialized non-field errors, then a generic fallback.
const extractGlobalSaveError = (response, fallback) => {
    const d = response?.data;
    if (typeof d === 'string') return d;
    if (d?.detail) return d.detail;
    if (Array.isArray(d?.non_field_errors) && d.non_field_errors.length) return d.non_field_errors.join('; ');
    if (d && typeof d === 'object' && Object.keys(d).length) {
        try { return JSON.stringify(d); } catch (_) { /* fall through */ }
    }
    return fallback;
};

export function handleGlobalTablePreferences(data) {
    return async () => {
        try {
            if (!data?.body?.table_name) {
                handleStopLoading();
                return null;
            }
            const response = await callApi('/api/v1/utils/globaltablecolumnpreference/', 'post', data.body);
            if (response.status === 201 || response.status === 200) {
                dispatch(slice.actions.upsertGlobalTablePreference(response.data));
                // Caller (handleSystemDefaultSave / handleResetSystemDefault) shows
                // its own snackbar — emitting success here too would double-toast.
                return response.data;
            }
            handleStopLoading();
            dispatch(updateError({ success: false, message: extractGlobalSaveError(response, 'Failed to save system default') }));
            return null;
        } catch (error) {
            handleStopLoading();
            dispatch(updateError({ success: false, message: error.message || 'Failed to save system default' }));
            return null;
        }
    };
}

export function handleGlobalTablePreferencesEdit(data) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/utils/globaltablecolumnpreference/${data?.tableId}/`, 'patch', data.body);
            if (response.status === 200) {
                dispatch(slice.actions.upsertGlobalTablePreference(response.data));
                // No success snackbar here — caller handles user-visible success.
                return response.data;
            }
            handleStopLoading();
            dispatch(updateError({ success: false, message: extractGlobalSaveError(response, 'Failed to update system default') }));
            return null;
        } catch (error) {
            handleStopLoading();
            dispatch(updateError({ success: false, message: error.message || 'Failed to update system default' }));
            return null;
        }
    };
}

export function handleGlobalTablePreferencesDelete(data) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/utils/globaltablecolumnpreference/${data?.tableId}/`,
                'delete',
            );
            if (response.status >= 400) {
                dispatch(slice.actions.setTablePreferenceLoading(false));
                dispatch(updateError({ success: false, message: 'Failed to reset system default — please try again' }));
                return false;
            }
            dispatch(slice.actions.removeGlobalTablePreference(data?.tableId));
            dispatch(slice.actions.setTablePreferenceLoading(false));
            return true;
        } catch (error) {
            dispatch(slice.actions.setTablePreferenceLoading(false));
            dispatch(updateError({ success: false, message: 'Failed to reset system default — please try again' }));
            return false;
        }
    };
}

export function handleTablePreferencesDelete(data) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${getPracticeId()}/users/${data?.practiceUserId}/table-column-preferences/${data?.tableId}/`,
                'delete'
            );
            if (response.status >= 400) {
                dispatch(slice.actions.setTablePreferenceLoading(false));
                dispatch(updateError({ success: false, message: 'Failed to reset column preferences — please try again' }));
                return;
            }
            // Re-fetch to reflect the deletion; on re-fetch failure, surface an error rather than silently stalling.
            try {
                await dispatch(getTablePreferences({ practiceUserId: data?.practiceUserId }));
            } catch (refetchError) {
                dispatch(updateError({ success: false, message: 'Column preferences reset but could not reload — please refresh' }));
            }
            dispatch(slice.actions.setTablePreferenceLoading(false));
        } catch (error) {
            dispatch(slice.actions.setTablePreferenceLoading(false));
            dispatch(updateError({ success: false, message: 'Failed to reset column preferences — please try again' }));
        }
    };
}

